import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {StringUtil} from "../../../util/string.util";
import {DocumentUtil} from "../../../util/document.util";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {ImagePreviewDialogComponent} from "./image-preview-dialog/image-preview-dialog.component";
import {BaseComponent} from '../../base.component';

@Component({
  selector: 'app-frontend-file-chooser',
  standalone: false,
  templateUrl: './frontend-file-chooser.component.html',
  styleUrls: ['./frontend-file-chooser.component.scss']
})
export class FrontendFileChooserComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input()
  title?: string;
  @Input()
  required: boolean = false;
  @Input()
  disabled?: boolean;
  @Input()
  accept: any;
  @Input()
  isMultiple: any = false;
  @Input()
  files: any = [];
  @Output()
  filesChange: EventEmitter<any> = new EventEmitter();
  blobUrls: Map<File, string> = new Map();
  isDragOver: boolean = false;
  // Security validation
  private allowedFileTypes: string[] = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
    'pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx',
    'zip', 'rar', 'mp4', 'avi', 'mov', 'mp3', 'wav'
  ];
  private maxFileSize: number = 10 * 1024 * 1024; // 10MB
  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  onFileDropped(event: any) {
    event.preventDefault();
    this.isDragOver = false;
    this.prepareFilesList(event.dataTransfer.files);
  }

  onDragOver(event: any) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: any) {
    event.preventDefault();
    this.isDragOver = false;
  }

  fileBrowseHandler(event: any) {
    this.prepareFilesList(event.target.files);
  }

  deleteFile(index: number) {
    const file = this.files[index];
    if (file instanceof File && this.blobUrls.has(file)) {
      URL.revokeObjectURL(this.blobUrls.get(file)!);
      this.blobUrls.delete(file);
    }
    this.files.splice(index, 1);
    if (this.isMultiple) {
      this.filesChange.emit(this.files);
    } else {
      if (this.files && this.files.length > 0) {
        this.filesChange.emit(this.files);
      } else {
        this.filesChange.emit(undefined);
      }
    }
  }

  prepareFilesList(files: any) {
    if (!this.isMultiple || !this.files) {
      this.files = [];
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        SnackBarUtil.makeMessage(this, validation.error || this.translateService.instant('InvalidFile'), false);
        continue;
      }
      this.files.push(file);
    }
    if (this.isMultiple) {
      this.filesChange.emit(this.files);
    } else {
      if (this.files && this.files.length > 0) {
        this.filesChange.emit(this.files);
      } else {
        this.filesChange.emit(undefined);
      }
    }
  }

  onFileClicked(index: number) {
    const file = this.files[index];
    const fileType = this.getFileType(file);
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileType)) {
      const imageSrc = this.getImageSrc(file);
      const imageName = file.name;
      this.matDialog.open(ImagePreviewDialogComponent, {
        data: {
          imageSrc: imageSrc,
          imageName: imageName
        },
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: 'auto',
        height: 'auto',
        panelClass: 'image-preview-dialog'
      });
    } else if (!(file instanceof File)) {
      this.serviceApi.download(file.name).subscribe((res: any) => {
        let response = res.body as Blob;
        let a = DocumentUtil.createElement('a');
        const objectUrl = URL.createObjectURL(response);
        a!.href = objectUrl;
        a!.download = StringUtil.getFormattedDate(new Date()) + '.' + StringUtil.getFileExtension(response.type);
        a!.click();
        URL.revokeObjectURL(objectUrl);
      });
    }
  }

  getImageSrc(file: any): string {
    if (file instanceof File) {
      if (!this.blobUrls.has(file)) {
        this.blobUrls.set(file, URL.createObjectURL(file));
      }
      return this.blobUrls.get(file)!;
    } else if (typeof file === 'string') {
      return file;
    } else if (file?.name) {
      return this.formatImage('', file.name);
    }
    return '';
  }

  getFileSize(file: any): string {
    if (file instanceof File) {
      const bytes = file.size;
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
    return '';
  }

  getFileType(file: any): string {
    if (file instanceof File) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return extension || 'unknown';
    } else if (file?.name) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return extension || 'unknown';
    }
    return 'unknown';
  }

  getFileIcon(file: any): string {
    const fileType = this.getFileType(file);
    const iconMap: { [key: string]: string } = {
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image',
      'gif': 'image',
      'bmp': 'image',
      'webp': 'image',
      'svg': 'image',
      'pdf': 'picture_as_pdf',
      'doc': 'description',
      'docx': 'description',
      'txt': 'text_snippet',
      'xls': 'table_chart',
      'xlsx': 'table_chart',
      'ppt': 'slideshow',
      'pptx': 'slideshow',
      'zip': 'folder_zip',
      'rar': 'folder_zip',
      'mp4': 'video_file',
      'avi': 'video_file',
      'mov': 'video_file',
      'mp3': 'audio_file',
      'wav': 'audio_file',
      'unknown': 'cloud_upload'
    };
    return iconMap[fileType] || 'cloud_upload';
  }

  ngOnDestroy() {
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    this.blobUrls.clear();
  }

  private validateFile(file: File): { isValid: boolean; error?: string } {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !this.allowedFileTypes.includes(fileExtension)) {
      return {
        isValid: false,
        error: `نوع فایل ${fileExtension || 'نامشخص'} مجاز نیست. فایل‌های مجاز: ${this.allowedFileTypes.join(', ')}`
      };
    }
    if (file.size > this.maxFileSize) {
      const maxSizeMB = this.maxFileSize / (1024 * 1024);
      return {
        isValid: false,
        error: `حجم فایل بیش از ${maxSizeMB}MB است. حداکثر حجم مجاز: ${maxSizeMB}MB`
      };
    }
    const dangerousTypes = ['exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar'];
    if (dangerousTypes.includes(fileExtension)) {
      return {
        isValid: false,
        error: 'این نوع فایل برای امنیت سایت مجاز نیست'
      };
    }
    return {isValid: true};
  }
}
